import { useCallback, useEffect, useState } from "react";
import {
  Breadcrumb, Button, Card, Col, Form, ListGroup, Row
} from "react-bootstrap";
import { useStateMachine } from "little-state-machine";
import {
  Divider, NoData, Spinner, Stepper
} from "@whitespace/whitespace-ui";
import { Link, useNavigate } from "react-router-dom";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { assignmentStepsHWRCRoleSuperAdmin, assignmentStepsHWRCRoleAdminLogisticsTeam, assignmentStepsHWRCRoleSite } from "modules/assignmentSteps";
import { useForm } from "react-hook-form";
import { FlowOrigination } from "interfaces/FlowOrigination";
import IClaimHwrc from "interfaces/responses/IClaimHwrc";
import { ClaimType } from "interfaces/models/ClaimType";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { updateAssignHwrcPermissionsRequest } from "actions/updateAssignHwrcPermissionsRequest";
import { initialAssignHwrcPermissionsRequest } from "modules/initialAssignHwrcPermissionsRequest";
import { updateAssignedClaimsHwrc } from "actions/updateAssignedClaimsHwrc";
import { mapHwrcClaimToRole } from "mappers/mapHwrcClaimToRole/mapHwrcClaimToRole";
import IRoleHwrc from "interfaces/models/IRoleHwrc";
import IAssignHwrcPermissionsRequest from "interfaces/requests/IAssignHwrcPermissionsRequest";
import { HwrcRoles } from "interfaces/HwrcRoles";
import { updateCurrentHwrcClaimsRoles } from "actions/updateCurrentHwrcClaimsRoles";
import UserRoleItem from "./UserRoleItem/UserRoleItem";

interface AssignUserHwrcRolesProps {
  flowOrigination : FlowOrigination
}

const AssignUserHwrcRoles = ({ flowOrigination } : AssignUserHwrcRolesProps) => {
  const navigate = useNavigate();

  const { actions } = useStateMachine({
    updateAssignHwrcPermissionsRequest,
    updateAssignedClaimsHwrc,
    updateCurrentHwrcClaimsRoles
  });

  const {
    isSending: rolesIsSending, errors: rolesError, data: rolesData, sendRequest: rolesSendRequest
  } = useAmplifyREST();

  const {
    isSending: assignedClaimsIsSending, errors: assignedClaimsError, data: assignedClaimsData, sendRequest: assignedClaimsSendRequest
  } = useAmplifyREST();

  const {
    isSending: addClaimsIsSending, errors: addClaimsErrors, statusCode: addClaimsStatusCode, sendRequest: addClaimsSendRequest
  } = useAmplifyREST();

  const {
    isSending: removeClaimsIsSending, errors: removeClaimsErrors, statusCode: removeClaimsStatusCode, sendRequest: removeClaimsSendRequest
  } = useAmplifyREST();

  const {
    isSending: allocateIsSending, errors: allocateErrors, statusCode: allocateStatusCode, sendRequest: allocateSendRequest, resetState: allocateResetState
  } = useAmplifyREST();

  const { state } = useStateMachine();
  const [selectionsMade, setSelectionsMade] = useState(false);
  const [roleSuperAdminSelected, setRoleSuperAdminSelected] = useState(false);
  const [roles, setRoles] = useState<IRoleHwrc[] | undefined>(undefined);
  const [claimsAssigned, setClaimsAssigned] = useState<IClaimHwrc[] | undefined>(undefined);
  const [stepperItems, setStepperItems] = useState<string[]>([]);
  const [ignoreClaimRemoval, setIgnoreClaimRemoval] = useState(false);

  const {
    register, handleSubmit, getValues, setValue, watch
  } = useForm({
    mode: "onSubmit"
  });

  const isSending = rolesIsSending || assignedClaimsIsSending || addClaimsIsSending || removeClaimsIsSending || allocateIsSending;
  const apiErrors = rolesError || assignedClaimsError || addClaimsErrors || removeClaimsErrors || allocateErrors;

  const watchFields = watch(["role"]);

  useEffect(() => {
    if (!state.assignHwrcPermissionsRequest && claimsAssigned) {
      const request = initialAssignHwrcPermissionsRequest();
      request.userId = state.selectedUser!.userId;
      request.applicationId = state.selectedUserApplicationDetails!.applicationDetails.applicationId;
      request.roleId = claimsAssigned.find((claim : IClaimHwrc) => claim.type === ClaimType.Role)?.id;
      request.regionIds = claimsAssigned.filter((claim : IClaimHwrc) => claim.type === ClaimType.Region).map((claim : IClaimHwrc) => claim.id);
      request.siteIds = claimsAssigned.filter((claim : IClaimHwrc) => claim.type === ClaimType.HwrcSite).map((claim : IClaimHwrc) => claim.id);

      actions.updateAssignHwrcPermissionsRequest(request);
    }
  }, [state.assignHwrcPermissionsRequest, claimsAssigned]);

  useEffect(() => {
    if (state.assignHwrcPermissionsRequest) {
      const roleSelected = getValues("role") as string;
      if (!roleSelected) {
        setSelectionsMade(false);
      } else {
        setSelectionsMade(true);
      }

      setRoleSuperAdminSelected(roleSelected === HwrcRoles.SuperAdmin);
      if (roleSelected) {
        switch (roleSelected) {
          case HwrcRoles.SuperAdmin: {
            if (stepperItems !== assignmentStepsHWRCRoleSuperAdmin) {
              setStepperItems(assignmentStepsHWRCRoleSuperAdmin);
            }
            break;
          }
          case HwrcRoles.Admin:
          case HwrcRoles.LogisticsTeam: {
            if (stepperItems !== assignmentStepsHWRCRoleAdminLogisticsTeam) {
              setStepperItems(assignmentStepsHWRCRoleAdminLogisticsTeam);
            }
            break;
          }
          case HwrcRoles.Site: {
            if (stepperItems !== assignmentStepsHWRCRoleSite) {
              setStepperItems(assignmentStepsHWRCRoleSite);
            }
            break;
          }
          default: {
            if (stepperItems.length !== 0) {
              setStepperItems([]);
            }
          }
        }
      }
    }
  }, [watchFields, state.assignHwrcPermissionsRequest]);

  useEffect(() => {
    // eslint-disable-next-line max-len
    const url = `apps/${encodeURIComponent(state.selectedUserApplicationDetails!.applicationDetails.applicationId)}/claim_types/roles/claims`;
    rolesSendRequest(url, "GET", null);
  }, [rolesSendRequest]);

  useEffect(() => {
    if (rolesData) {
      actions.updateCurrentHwrcClaimsRoles(rolesData as IClaimHwrc[]);
      setRoles(
        rolesData.map(
          (claim : IClaimHwrc) => mapHwrcClaimToRole(claim)
        )
      );
    }
  }, [rolesData]);

  useEffect(() => {
    // eslint-disable-next-line max-len
    const url = `apps/${encodeURIComponent(state.selectedUserApplicationDetails!.applicationDetails.applicationId)}/users/${encodeURIComponent(state.selectedUser!.userId)}/claims`;
    assignedClaimsSendRequest(url, "GET", null);
  }, [assignedClaimsSendRequest]);

  useEffect(() => {
    if (assignedClaimsData) {
      actions.updateAssignedClaimsHwrc(assignedClaimsData as IClaimHwrc[]);

      setClaimsAssigned(assignedClaimsData as IClaimHwrc[]);
    }
  }, [assignedClaimsData]);

  const renderRoles = () => roles?.map((role : IRoleHwrc, idx : number) => (
    <UserRoleItem register={register} key={`userRoleItem_${idx}`} role={role} />
  ));

  const constructRequestFromForm = useCallback(() => {
    if (state.assignHwrcPermissionsRequest) {
      const request : IAssignHwrcPermissionsRequest = initialAssignHwrcPermissionsRequest();
      request.applicationId = state.assignHwrcPermissionsRequest!.applicationId;
      request.userId = state.assignHwrcPermissionsRequest!.userId;
      request.regionIds = state.assignHwrcPermissionsRequest!.regionIds;
      request.siteIds = state.assignHwrcPermissionsRequest!.siteIds;

      request.roleId = getValues("role") as string;

      actions.updateAssignHwrcPermissionsRequest(request);
    }
  }, [getValues, state.assignHwrcPermissionsRequest]);

  const applySuperUserRole = () => {
    // eslint-disable-next-line max-len
    const url = `apps/${encodeURIComponent(state.selectedUserApplicationDetails!.applicationDetails.applicationId)}/users/${encodeURIComponent(state.selectedUser!.userId)}/claims`;
    const roleClaims = rolesData as IClaimHwrc[];
    const claims : IClaimHwrc[] = [];

    claims.push(roleClaims.find((claim : IClaimHwrc) => claim.id === HwrcRoles.SuperAdmin)!);
    addClaimsSendRequest(url, "PUT", claims);
  };

  const toggleAllocated = () => {
    const method = "PUT";
    // eslint-disable-next-line max-len
    const url = `users/${encodeURIComponent(state.selectedUser!.userId)}/apps/${encodeURIComponent(state.selectedUserApplicationDetails!.applicationDetails.applicationId)}`;
    allocateSendRequest(url, method);
  };

  const removeClaims = () => {
    const claimsToRemove = state.assignedClaimsHwrc!;

    if (claimsToRemove.length > 0) {
    // eslint-disable-next-line max-len
      const url = `apps/${encodeURIComponent(state.selectedUserApplicationDetails!.applicationDetails.applicationId)}/users/${encodeURIComponent(state.selectedUser!.userId)}/claims`;
      removeClaimsSendRequest(url, "DELETE", claimsToRemove);
    } else {
      setIgnoreClaimRemoval(true);
    }
  };

  const updateClaims = () => {
    removeClaims();
    applySuperUserRole();
  };

  const handleSaveClick = () => {
    constructRequestFromForm();
    updateClaims();
    toggleAllocated();
  };

  useEffect(() => {
    if ((removeClaimsStatusCode === 204 || ignoreClaimRemoval) && addClaimsStatusCode === 204 && allocateStatusCode === 204) {
      if (flowOrigination === FlowOrigination.UserManagement) {
        navigate("/manageuserapplications");
      } else {
        navigate("/requests");
      }
    }
  }, [removeClaimsStatusCode, addClaimsStatusCode, allocateStatusCode]);

  const setControlValues = useCallback(() => {
    if (state.assignHwrcPermissionsRequest) {
      setValue("role", state.assignHwrcPermissionsRequest.roleId);
    }
  }, [setValue, state.assignHwrcPermissionsRequest]);

  useEffect(() => {
    setControlValues();
  }, [setControlValues]);

  const handleSelectClick = () => {
    constructRequestFromForm();

    if (flowOrigination === FlowOrigination.UserManagement) {
      navigate("/assignuserhwrcregions");
    } else {
      navigate("/requestsassignuserhwrcregions");
    }
  };

  const handleBackButtonClick = () => {
    if (flowOrigination === FlowOrigination.UserManagement) {
      navigate("/manageuserapplications");
    } else {
      navigate("/requests");
    }
  };

  return (
    <>
      {apiErrors && <AmplifyError errors={apiErrors} />}
      <Card>
        <Card.Header>
          <Row className="w-100">
            <Col xs={8}>
              <h1>
                Assign Roles
                <Spinner animation="grow" visible={isSending} spinnerText="Loading" variant="info" />
              </h1>
            </Col>
            <Col xs={4} className="d-flex justify-content-end">
              <span data-testid="selected-user-title" className="selected-user-title">{state.selectedUser?.username}</span>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="no-padding">
          <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
            <Col xs={8} className="d-flex">
              <Breadcrumb className="breadcrumb-manage-user left-margin-large no-wrap">
                {flowOrigination === FlowOrigination.Requests
            && (
              <>
                <Breadcrumb.Item
                  data-testid="breadcrumb-requests"
                  linkAs={Link}
                  linkProps={{ to: "/requests" }}
                  onClick={() => navigate("/requests")}
                >
                  Requests
                </Breadcrumb.Item>
                <Breadcrumb.Item data-testid="breadcrumb-site-url" active>
                  {state.selectedUserApplicationDetails!.applicationDetails.siteUrl}
                </Breadcrumb.Item>
                <Breadcrumb.Item active>
                  Roles
                </Breadcrumb.Item>

              </>
            )}
                {flowOrigination === FlowOrigination.UserManagement
            && (
              <>
                <Breadcrumb.Item
                  data-testid="breadcrumb-users"
                  linkAs={Link}
                  linkProps={{ to: "/users" }}
                  onClick={() => navigate("/users")}
                >
                  Users
                </Breadcrumb.Item>

                <Breadcrumb.Item
                  data-testid="breadcrumb-manage-user"
                  linkAs={Link}
                  linkProps={{ to: "/manageuser" }}
                  onClick={() => navigate("/manageuser")}
                >
                  Manage User
                </Breadcrumb.Item>
                <Breadcrumb.Item
                  data-testid="breadcrumb-manage-user-applications"
                  linkAs={Link}
                  linkProps={{ to: "/manageuserapplications" }}
                  onClick={() => navigate("/manageuserapplications")}
                >
                  Applications
                </Breadcrumb.Item>
                <Breadcrumb.Item data-testid="breadcrumb-site-url" active>
                  {state.selectedUserApplicationDetails!.applicationDetails.siteUrl}
                </Breadcrumb.Item>
                <Breadcrumb.Item data-testid="breadcrumb-roles" active>
                  Roles
                </Breadcrumb.Item>

              </>
            )}
              </Breadcrumb>
            </Col>
          </Row>
          <Divider />
          <Form noValidate onSubmit={handleSubmit(handleSelectClick)}>
            <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={8} className="d-flex">
                {stepperItems.length > 0 && (
                  <span data-testid="stepper-container" className="w-100">
                    <Stepper className="w-100 ms-4 mb-0" steps={stepperItems} currentStep={0} />
                  </span>
                )}
              </Col>
              <Col xs={4} className="d-flex justify-content-end">
                <Button variant="secondary" className="me-2" onClick={() => handleBackButtonClick()}>
                  Back
                </Button>
                {!roleSuperAdminSelected
                && (
                  <Button data-testid="btnSelect" variant="primary" type="submit" disabled={!selectionsMade || isSending}>
                    Select
                  </Button>
                )}
                {roleSuperAdminSelected
                && (
                  <Button data-testid="btnSave" variant="primary" type="button" onClick={() => handleSaveClick()} disabled={!selectionsMade || isSending}>
                    Save
                  </Button>
                ) }
              </Col>
            </Row>
            <Divider />
            {roles && claimsAssigned && (
              <ListGroup variant="flush" className="striped highlight">
                <NoData visible={roles.length === 0} description="No Roles Found" />
                {renderRoles()}
              </ListGroup>
            )}
          </Form>
        </Card.Body>
      </Card>
    </>
  );
};

export default AssignUserHwrcRoles;
